import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import ReportBlock from "./ReportBlock";
import { reportsData } from "../../utils/apis";
import { Box, Flex, Image, Text } from "@mantine/core";
import { useStylesPage } from "../../utils/mantine/useStylesPage";
import { useStylesReports } from "../../utils/mantine/useStylesReports";
import CustomAnchor from "../mantineCustomComponents/CustomAnchor";

import { useMantineTheme } from "@mantine/core";

import {
  reportsTableColumns,
  reportsTableData,
  reportsTableOptions,
} from "../../utils/reports/reportsTable";
import { MantineReactTable, useMantineReactTable } from "mantine-react-table";
import { useTableIcons } from "../../utils/collectionFound/iconsUtil";
import { useStylesTable } from "../../utils/mantine/useStylesTable";

const ReportContent = ({ staticBasePath }) => {
  const theme = useMantineTheme();

  //css classes
  const { classes: classesPage } = useStylesPage();

  const { classes: reportsClasses } = useStylesReports();

  const { classes: tableClasses } = useStylesTable();

  // Initial state for input values
  const [inputValues, setInputValues] = useState({
    allocations: [],
    current_page: 1,
    total_pages: 1,
    producer_identity: {
      name: "",
      url: "",
    },
    strategy_name: "",
    press_release_pdf: "",
    validation_information: {
      strategy_commits: "",
      other_strategies_commits: "",
      total_commits: "",
    },
    available_for_download: false,
    download_ZIP: "",
    download_single_CSV: "",
  });

  useEffect(() => {
    reportsData(1, setInputValues);
  }, []);

  // Icons used within the tables, provided through a custom utility function
  const faIcons = useMemo(() => useTableIcons(), []);

  const dataReports = useMemo(
    () => reportsTableData(inputValues["allocations"], theme),
    [inputValues["allocations"]],
  );

  // Column definitions for the main table using a utility function
  const columnsReports = reportsTableColumns();

  // Configuration options for the main table using Mantine's table library
  const tableReports = useMantineReactTable(
    reportsTableOptions(columnsReports, dataReports, faIcons, theme),
  );
  return (
    <Box className={reportsClasses.reports}>
      <Box className={`${classesPage.header} ${reportsClasses.reportsHeader}`}>
        <Text
          className={`${classesPage.headerTitle} ${reportsClasses.reportsTitle}`}
        >
          Model Performance Authentication
        </Text>
        <Text
          className={`${classesPage.headerText} ${classesPage.headerTextNoHeight} ${reportsClasses.reportsText}`}
        >
          The vBase app verifiably secures data, creating a timestamped record
          of your data’s existence. Build your credibility. Anyone with whom you
          share your file can validate its timestamp and integrity by checking
          the blockchain fingerprint.
        </Text>
      </Box>
      <Box className={reportsClasses.reportsBlock}>
        <Flex
          className={reportsClasses.reportsBlockInner}
          justify={theme.flex.justify.between}
          wrap="wrap"
        >
          <Box>
            <Box className={reportsClasses.reportsBlockTitle}>
              Strategy Information
            </Box>

            <Box className={reportsClasses.reportsBlockItem}>
              <Box className={reportsClasses.reportsBlockKey}>
                Strategy Producer Identity:
              </Box>
              <Box component="b" className={reportsClasses.reportsBlockValue}>
                {inputValues.producer_identity?.name}
              </Box>
              {inputValues.producer_identity?.url && (
                <>
                  (
                  <CustomAnchor
                    text="Producer Profile"
                    href={inputValues.producer_identity.url}
                    componentClasses="link"
                  />
                  )
                </>
              )}
            </Box>

            <Box className={reportsClasses.reportsBlockItem}>
              <Box className={reportsClasses.reportsBlockKey}>
                Strategy Name:
              </Box>

              <Box component="b" className={reportsClasses.reportsBlockValue}>
                {inputValues.strategy_name}
              </Box>
            </Box>

            <Box className={reportsClasses.reportsBlockItem}>
              <Box className={reportsClasses.reportsBlockKey}>
                Press Release Issued:
              </Box>

              {inputValues.press_release_pdf && (
                <CustomAnchor
                  text={inputValues.strategy_name}
                  href={inputValues.press_release_pdf}
                  componentClasses="link"
                />
              )}
            </Box>
          </Box>
          <Box>
            <Box
              className={`${reportsClasses.reportsBlockTitle} ${reportsClasses.reportsBlockContentTitle}`}
            >
              Validation Information
            </Box>
            <Flex
              gap={theme.flex.gap.base}
              className={reportsClasses.reportsBlockWrap}
            >
              {inputValues?.validation_information?.strategy_commits && (
                <ReportBlock
                  background={theme.customBackgrounds.reportsBlock}
                  color={theme.customColors.white}
                  title="Strategy Commits"
                  number={inputValues?.validation_information?.strategy_commits}
                  text="Strategy Commits Strategy Commits Strategy Commits Strategy Commits Strategy Commits"
                />
              )}

              {inputValues?.validation_information
                ?.other_strategies_commits && (
                <ReportBlock
                  background={theme.customBackgrounds.reportsBlock2}
                  color={theme.customColors.black}
                  title="Other Strategies commits"
                  number={
                    inputValues?.validation_information
                      ?.other_strategies_commits
                  }
                  text="Other Strategies commits Other Strategies commits Other Strategies commits Other Strategies commits"
                />
              )}
              {inputValues?.validation_information?.total_commits && (
                <ReportBlock
                  background={theme.customBackgrounds.reportsBlock3}
                  color={theme.customColors.white}
                  title="Total Commits"
                  number={inputValues?.validation_information?.total_commits}
                  text="Total Commits Total Commits Total Commits Total Commits Total Commits "
                />
              )}
            </Flex>
            <Box className={reportsClasses.reportsAvailableDownload}>
              <Box className={reportsClasses.reportsAvailableDownloadTitle}>
                all files available for download:
              </Box>
              <Flex
                align={theme.flex.align.center}
                className={reportsClasses.reportsAvailableDownloadContent}
              >
                {inputValues?.available_for_download ? (
                  <>
                    <Flex
                      justify={theme.flex.justify.center}
                      align={theme.flex.align.center}
                      className={
                        reportsClasses.reportsAvailableDownloadContentImg
                      }
                    >
                      <Image
                        src={`${staticBasePath}public/images/svg/yes.svg`}
                        alt="Yes"
                      />
                    </Flex>
                    YES
                  </>
                ) : (
                  <>
                    <Flex
                      justify={theme.flex.justify.center}
                      align={theme.flex.align.center}
                      className={`${reportsClasses.reportsAvailableDownloadContentImg} ${reportsClasses.reportsAvailableDownloadContentNo}`}
                    >
                      <Image
                        src={`${staticBasePath}public/images/svg/no.svg`}
                        alt="No"
                      />
                    </Flex>
                    NO
                  </>
                )}
              </Flex>
            </Box>
          </Box>
        </Flex>

        <Flex
          justify={theme.flex.justify.between}
          className={reportsClasses.reportsTableTitleWrap}
        >
          <Box className={reportsClasses.reportsTableTitle}>
            Committed target allocations
          </Box>
          <Flex className={reportsClasses.reportsTableLinks}>
            {inputValues.download_ZIP && (
              <CustomAnchor
                text="Download ZIP"
                href={inputValues.download_ZIP}
                componentClasses="link"
              />
            )}

            {inputValues.download_single_CSV && (
              <CustomAnchor
                text=" Download Single CSV"
                href={inputValues.download_single_CSV}
                componentClasses="link"
              />
            )}
          </Flex>
        </Flex>
        <Box className={tableClasses.table}>
          <MantineReactTable
            columns={reportsTableColumns()}
            data={reportsTableData}
            mb={theme.margin.mdSpacious}
            table={tableReports}
          />
        </Box>
      </Box>
    </Box>
  );
};
ReportContent.propTypes = {
  staticBasePath: PropTypes.string.isRequired,
};
export default ReportContent;
